/**
 * ServerError - 500 error page
 * Displays when server errors occur
 * Provides recovery options and support contact
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import { apiService } from '../services/apiService';
import { logger } from '../utils/logger';
import SEO from '../components/seo/SEO';

interface ServerErrorProps {
  error?: string;
  onRetry?: () => void;
}

export default function ServerError({ error, onRetry }: ServerErrorProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleReload = () => {
    window.location.reload();
  };

  const handleReportError = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await apiService.post('/error-reports', {
        url: window.location.href,
        error_message: error || 'Unknown error',
        browser_info: navigator.userAgent,
        page_state: JSON.stringify({
          timestamp: new Date().toISOString(),
          path: window.location.pathname
        })
      });
      setSubmitted(true);
    } catch (err) {
      logger.child('UI').error('Failed to submit error report:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <SEO
        title="Server Error - PromptShare"
        description="We're experiencing technical difficulties. Please try again later."
        noindex={true}
      />
      <div className="max-w-md w-full text-center">
        {/* Error Illustration */}
        <div className="mb-8">
          <div className="text-6xl font-bold text-red-600 mb-4">500</div>
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Content */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          Server Error
        </h1>

        <p className="text-gray-600 mb-8">
          We're experiencing some technical difficulties. 
          Don't worry, Use below option to notify the issue and our team will fix it.
        </p>

        {/* Error Details */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {onRetry && (
            <Button
              onClick={onRetry}
              variant="primary"
              size="lg"
            >
              Try Again
            </Button>
          )}
          
          <Button
            onClick={handleReload}
            variant="outline"
            size="lg"
          >
            Reload Page
          </Button>
        </div>

        {/* Support */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                Thank you! We've received your error report and will investigate.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Need help? Contact our support team:
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <button
                  onClick={handleReportError}
                  disabled={isSubmitting}
                  className="text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Reporting...' : 'Report This Error'}
                </button>
                <a
                  href="mailto:contact@promptshare.pro"
                  className="text-primary-600 hover:text-primary-700"
                >
                  Email Support
                </a>
                <Link
                  to="/"
                  className="text-primary-600 hover:text-primary-700"
                >
                  Go Home
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}